import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Radio } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDateFull, fmtDuration, timeAgo, useNow } from "@/lib/format";
import { LiveDuration, PageHeader, Stat, UserAvatar } from "@/components/common/Bits";
import { Tag } from "@/components/common/StatusChip";

function WorkedToday({ base, from }) {
  const now = useNow();
  return <div className="mono text-sm font-semibold text-emerald-600 dark:text-emerald-400">{fmtDuration(base + (now - new Date(from).getTime()) / 1000)}</div>;
}

export default function Board() {
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ["board"], queryFn: () => api.get("/board").then((r) => r.data), refetchInterval: 5000 });
  const users = data?.users || [];
  return (
    <div className="flex h-full flex-col" data-testid="live-board-page">
      <PageHeader eyebrow="Live" title={<span className="flex items-center gap-2"><Radio className="h-5 w-5 text-emerald-500" /> Who is working what</span>} description="Refreshes every 5 seconds. Durations tick live from the moment someone confirmed “Start working”." />
      <div className="grid grid-cols-2 gap-px border-b border-border bg-border md:grid-cols-5">
        <Stat label="Working now" value={data?.working_count ?? "–"} tone="text-emerald-500" testId="board-working-count" />
        <Stat label="Active users" value={users.length} testId="board-user-count" />
        <Stat label="Unassigned open" value={data?.unassigned_open ?? 0} tone={data?.unassigned_open ? "text-amber-500" : ""} testId="board-unassigned" />
        <Stat label="Idle users" value={users.filter((u) => !u.current).length} testId="board-idle" />
        <Stat label="Worked today (all)" value={fmtDuration(users.reduce((a, u) => a + (u.worked_today_seconds || 0), 0))} testId="board-worked-today" />
      </div>
      <div className="grid flex-1 gap-4 p-4 md:p-6 xl:grid-cols-[1fr_360px]">
        <div className="grid content-start gap-3 sm:grid-cols-2 2xl:grid-cols-3" data-testid="board-grid">
          {users.map((u) => (
            <div key={u.user_id} className={`surface fade-up p-4 ${u.current ? "border-emerald-500/50" : ""}`} data-testid={`board-user-${u.user_id}`}>
              <div className="flex items-center gap-3">
                <UserAvatar name={u.name} src={u.avatar_url} size="lg" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-bold">{u.name}</div>
                  <div className="mono truncate text-[11px] text-muted-foreground">{u.email}</div>
                </div>
                {u.current ? <span className="live-dot" /> : <span className="h-2 w-2 rounded-full bg-muted-foreground/40" />}
              </div>
              {u.current ? (
                <button onClick={() => navigate(`/tickets/${u.current.ticket_id}`)} className="mt-3 block w-full border border-emerald-500/40 bg-emerald-500/10 p-3 text-left transition-colors hover:bg-emerald-500/20" data-testid={`board-current-${u.user_id}`}>
                  <div className="flex items-center justify-between">
                    <span className="mono text-[11px] font-semibold text-emerald-600 dark:text-emerald-300">{u.current.number}</span>
                    <LiveDuration from={u.current.started_at} clock className="text-lg font-semibold text-emerald-600 dark:text-emerald-300" />
                  </div>
                  <div className="mt-1 truncate text-xs font-medium">{u.current.subject}</div>
                  <div className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground"><span className="h-2 w-2" style={{ background: u.current.channel_color }} />{u.current.channel_name} · since {fmtDateFull(u.current.started_at)}</div>
                </button>
              ) : (
                <div className="mt-3 border border-dashed border-border p-3 text-xs text-muted-foreground">Not working a ticket · last activity {timeAgo(u.last_activity_at)}</div>
              )}
              <div className="mt-3 grid grid-cols-3 gap-px bg-border text-center">
                <div className="bg-card py-1.5"><div className="mono text-sm font-semibold">{u.open_count}</div><div className="eyebrow">open</div></div>
                <div className="bg-card py-1.5"><div className="mono text-sm font-semibold">{u.closed_today}</div><div className="eyebrow">closed today</div></div>
                <div className="bg-card py-1.5" data-testid={`board-worked-today-${u.user_id}`}>
                  {u.current ? <WorkedToday base={u.worked_today_seconds} from={u.current.started_at} /> : <div className="mono text-sm font-semibold">{u.worked_today_seconds ? fmtDuration(u.worked_today_seconds) : "0s"}</div>}
                  <div className="eyebrow">worked today</div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <aside className="surface h-fit p-4" data-testid="board-reassignments">
          <div className="eyebrow mb-2">Inactivity reassignments · last 48h</div>
          {!data?.recent_reassignments?.length && <p className="text-xs text-muted-foreground">None. Everyone is keeping up.</p>}
          <ul className="space-y-3">
            {data?.recent_reassignments?.map((e) => (
              <li key={e.id} className="text-xs">
                <button onClick={() => navigate(`/tickets/${e.ticket_id}`)} className="mono font-semibold hover:underline">{e.ticket_number}</button> {e.details?.from_name} → <strong>{e.details?.to_name}</strong>
                <div className="mt-0.5 flex items-center gap-2"><Tag tone="amber">{e.details?.tag}</Tag><span className="text-muted-foreground">{timeAgo(e.at)}</span></div>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </div>
  );
}
